import { CustomIntervalType as CustomIntervalTypeEnum } from "@sinch/core/entities/serverEnums";
import { CustomIntervalType } from "@sinch/types/sinch.types";
import { t } from "i18next";
import { boolean, number, object, ObjectSchema } from "yup";
import { useEntityProps } from "../utilsHooks";
import { intervalTypeTimeSchema } from "@sinch/components/application/TimeIntervalSettings/formSchema";

type IntervalType = CustomIntervalType | CustomIntervalTypeEnum;

export const staticCustomIntervalTypeSchemas = {
  [CustomIntervalTypeEnum.CzHolidays]: object().shape({
    includeSaturday: boolean().default(false),
    includeSunday: boolean().default(false),
  }),

  [CustomIntervalTypeEnum.DailyOvertime]: object({
    limit: number().min(0).default(0),
  }),

  [CustomIntervalTypeEnum.Time]: intervalTypeTimeSchema,

  [CustomIntervalTypeEnum.WeeklyOvertime]: object({
    limit: number().min(0).default(0),
    resetDay: number().min(1).max(7).default(1),
  }),

  [CustomIntervalTypeEnum.WorkedDuration]: object({
    fromAmountOfHours: number().min(0).default(0),
    toAmountOfHours: number()
      .min(0)
      .nullable()
      .when("fromAmountOfHours", ([fromAmount], schema) => (fromAmount ? schema.min(fromAmount) : schema))
      .default(null),
  }),
};

/**
 * Custom hook to manage condition operators.
 */
export const useCustomIntervalType = () => {
  const operators: Record<IntervalType, { label: string; schema: ObjectSchema<any>; description: string }> = {
    [CustomIntervalTypeEnum.CzHolidays]: {
      label: t("CustomIntervalType.czHolidays"),
      description: t("CustomIntervalType.czHolidaysDescription"),
      schema: staticCustomIntervalTypeSchemas[CustomIntervalTypeEnum.CzHolidays],
    },
    [CustomIntervalTypeEnum.DailyOvertime]: {
      label: t("CustomIntervalType.dailyOvertime"),
      description: t("CustomIntervalType.dailyOvertimeDescription"),
      schema: staticCustomIntervalTypeSchemas[CustomIntervalTypeEnum.DailyOvertime],
    },
    [CustomIntervalTypeEnum.Time]: {
      label: t("CustomIntervalType.time"),
      description: t("CustomIntervalType.timeDescription"),
      schema: staticCustomIntervalTypeSchemas[CustomIntervalTypeEnum.Time],
    },
    [CustomIntervalTypeEnum.WeeklyOvertime]: {
      label: t("CustomIntervalType.weeklyOvertime"),
      description: t("CustomIntervalType.weeklyOvertimeDescription"),
      schema: staticCustomIntervalTypeSchemas[CustomIntervalTypeEnum.WeeklyOvertime],
    },
    [CustomIntervalTypeEnum.WorkedDuration]: {
      label: t("CustomIntervalType.workedDuration"),
      description: t("CustomIntervalType.workedDurationDescription"),
      schema: staticCustomIntervalTypeSchemas[CustomIntervalTypeEnum.WorkedDuration],
    },
  };
  const getProp = useEntityProps(operators);

  const getCustomIntervalLabel = getProp<string>("label");
  const getCustomIntervalDescription = getProp<string>("description");
  const getCustomIntervalSchema = getProp<ObjectSchema<any>>("schema");

  const getOptionsList = () =>
    Object.entries(operators).map(([key, value]) => ({
      value: key,
      label: value.label,
    }));

  return { getCustomIntervalLabel, getCustomIntervalSchema, getOptionsList, getCustomIntervalDescription };
};
